// =============================================================================
// weapons.ts — Armas do jogo: dados e curvas de escala por nível (1 a 8).
// O COMPORTAMENTO de cada tipo vive no servidor (src/server/domain/weapons);
// aqui ficam só os números e textos, compartilhados com o cliente (HUD, urna
// e auras desenhadas localmente).
// As descrições são CONCRETAS (issue #31): o que a arma faz, os números base
// (dano, recarga, alcance/projéteis) e o que melhora por nível. Os ícones são
// pixel art procedural do cliente (ui/pixelIcons.ts) — zero emoji.
// Issue #121: teto de nível subiu de 5 pra 8 e as armas iniciais ganharam
// marcos por nível (nv 3 desbloqueia X, nv 6 desbloqueia Y...).
// =============================================================================

export const MAX_WEAPON_LEVEL = 8;
export const MAX_WEAPONS_PER_PLAYER = 4;

// Helper de escala linear: base * (1 + fator*(lvl-1))
const lin = (base: number, fator: number) => (lvl: number) => base * (1 + fator * (lvl - 1));

// -----------------------------------------------------------------------------
// Stats efetivos por tipo de arma (retorno de `scale(lvl)`)
// -----------------------------------------------------------------------------
export interface ConeStats {
  damage: number;
  cooldown: number;
  range: number;
  arc: number;
  knockback?: number;   // empurrão do soluço
  stunChance?: number;  // chance de atordoar (congela curto)
  stunDur?: number;
  bursts?: number;      // soluços por disparo (duplo/triplo)
  scatter?: boolean;    // rajadas em direções aleatórias (Crise de Soluço)
}

export interface BounceStats {
  damage: number;
  cooldown: number;
  count: number;
  bounces: number;
  speed: number;
  bounceBonus: number;  // +dano por quique
  split: boolean;       // cada quique cria uma cópia (fake news)
  maxSplits?: number;
  splitGen?: number;    // gerações de divisão: 2 = as cópias também se dividem (deep fake)
  homing?: boolean;     // o projétil corrige a rota e persegue o alvo (deep fake)
  pierce?: boolean;     // atravessa a fila em vez de quicar (carteira de trabalho)
  boomerang?: boolean;  // vai até `range`, dá meia-volta e re-acerta no retorno
  range?: number;       // distância de ida do bumerangue
  returnMult?: number;  // multiplicador de dano no retorno (carteira: dobra)
  maxHits?: number;     // pierce limitado: quantos atravessa por perna (0 = sem teto)
  trailDps?: number;    // papéis cortantes: rastro de zonas de dps pelo caminho
  finalBlast?: number;  // raio da explosão de documentos ao voltar pra mão
  radial?: boolean;     // leque completo em 360 graus (CLT Reverso 6x1)
  aim: 'facing' | 'nearest'; // 'facing': tiro reto na direção das setas; 'nearest': teleguiado
}

export interface LobStats {
  damage: number;      // dano do estilhaço na aterrissagem
  cooldown: number;
  count: number;       // pães/cadeiras por arremesso
  range: number;       // alcance máximo do arremesso
  speed: number;       // velocidade do voo
  radius: number;      // raio da poça
  puddleDps: number;   // dps de quem fica na poça
  puddleDur: number;   // duração da poça
  spread?: number;     // poças-filhas que brotam ao redor do pouso (banquete)
  slowMult?: number;   // poça de mortadela: lentidão de quem pisa (0.55 = -45%)
  pull?: number;       // px/s de atração pro centro da poça (nv 8 do pão)
  knockback?: number;  // empurrão no estouro (cadeirada nv 5+)
  stun?: number;       // segundos de atordoamento no estouro (cadeirada nv 8)
  flightDamage?: number; // dano de contato do objeto EM VOO (cadeira atravessa)
  flightHits?: number;   // quantos inimigos o voo atropela antes de virar fantasma
}

export interface WaveStats {
  damage: number;
  cooldown: number;
  radius: number;
  knockback: number;
  pulses?: number;     // ondas extras por disparo
  pulseDelay?: number; // intervalo entre as ondas extras
}

export interface GustStats {
  damage: number;
  cooldown: number;
  range: number;        // distância de voo da rajada
  speed: number;
  width: number;        // largura da rajada (diâmetro de colisão)
  knockback: number;
  count: number;        // 1 rajada frontal; 3 = laterais extras (nv 5)
  tornadoChance: number; // chance da rajada deixar um mini-tornado ao morrer
  tornadoDps: number;
  tornadoDur: number;
  tornadoPull: number;  // px/s de sucção do tornado
  tornadoRadius: number;
  ricochet: boolean;    // rajada ricocheteia nas bordas da arena (nv 8)
  tornado?: boolean;    // Tornado da Nova Matriz: só tornados errantes, sem rajada
}

export interface DashStats {
  damage: number;
  cooldown: number;
  dist: number;
  width: number;
  count: number;
}

export interface ConvertStats {
  cooldown: number;
  cost: number;         // votos (XP da chapa) gastos por conversão
  duration: number;
  maxAllies: number;
  allyDmgMult: number;
}

export interface SummonStats {
  cooldown: number;
  allyHp: number;
  allyDps: number;
  maxAllies: number;
  duration: number;
}

export interface TrapStats {
  dps: number;
  cooldown: number;
  radius: number;
  rootDur: number;
  castRange: number;     // distância máxima onde a raiz brota
  vuln?: boolean;        // presos tomam +dano de TODAS as armas (nv 6)
  treeChance?: number;   // chance de brotar árvore-barreira (nv 7)
  trees?: number;        // árvores por brote (Floresta em Pé: 3)
  treeDur?: number;
  backlashChance: number;
  backlashDur: number;
}

export interface OrbitStats {
  dps: number;
  beams: number;
  radius: number;
  beamLen: number;
  rotSpeed: number;     // rad/s
  rings?: number;       // órbitas concêntricas (a externa gira ao contrário)
  reflect?: boolean;    // reflete projéteis hostis que encostam na órbita
  push?: number;        // cruzes empurram inimigos (força por segundo, nv 6)
}

export interface LeechStats {
  dps: number;          // sangria por segundo do morcego grudado
  cooldown: number;
  count: number;        // morcegos simultâneos em campo
  speed: number;
  range: number;        // alcance de caça a partir do jogador
  healRatio?: number;   // fração do dano sugado que vira cura pro dono
  chain?: boolean;      // alvo morreu? o morcego emenda a próxima mordida (nv 6)
  deathBlast?: number;  // raio da explosão de sombra quando o alvo morre (nv 7)
  deathBlastDmg?: number;
  targetHighHp?: boolean; // prioriza o inimigo com mais HP (nv 8)
}

export interface DrainStats {
  dps: number;
  radius: number;
  healRatio: number;
  healCap: number;      // teto de cura em HP/s — horda densa não vira imortalidade
  buffDmg?: number;     // aliados no raio ganham +X de dano
  buffDur?: number;
}

export interface NovaStats {
  damage: number;
  cooldown: number;
  radius: number;
  knockback: number;
}

export interface FreezeStats {
  cooldown: number;
  radius: number;
  duration: number;
  damage: number;
  selfFreezeChance: number;
  selfFreezeDur: number;
}

// -----------------------------------------------------------------------------
// Evolução (issue #98): arma no nível MÁXIMO (8) + passiva correspondente em
// qualquer nível = arma SUPREMA, entregue pelo Cassino da Maleta (GOLPE DE
// MESTRE). A receita mora na arma base; a evoluída tem nível único (`evolved`).
// -----------------------------------------------------------------------------
export interface WeaponEvolution {
  passive: string;   // id da passiva exigida (shared/upgrades.ts)
  into: string;      // id da arma suprema resultante (EVOLVED_WEAPONS)
}

// -----------------------------------------------------------------------------
// Definição de arma (união discriminada pelo `tipo`)
// -----------------------------------------------------------------------------
interface WeaponBase<K extends string, S> {
  id: string;
  nome: string;
  tipo: K;
  desc: string;
  evolution?: WeaponEvolution;  // receita: esta arma nv 8 + passiva => suprema
  evolved?: boolean;            // arma suprema: nível único, fora da urna
  scale(lvl: number): S;
}

export type WeaponDef =
  | WeaponBase<'cone', ConeStats>
  | WeaponBase<'bounce', BounceStats>
  | WeaponBase<'lob', LobStats>
  | WeaponBase<'wave', WaveStats>
  | WeaponBase<'gust', GustStats>
  | WeaponBase<'dash', DashStats>
  | WeaponBase<'convert', ConvertStats>
  | WeaponBase<'summon', SummonStats>
  | WeaponBase<'trap', TrapStats>
  | WeaponBase<'orbit', OrbitStats>
  | WeaponBase<'leech', LeechStats>
  | WeaponBase<'drain', DrainStats>
  | WeaponBase<'nova', NovaStats>
  | WeaponBase<'freeze', FreezeStats>;

export type WeaponKind = WeaponDef['tipo'];

export const WEAPONS = {
  // --------------------------------------------------------------------------
  // ARMAS INICIAIS (uma por personagem) — issue #121
  // --------------------------------------------------------------------------
  live_varanda: {
    id: 'live_varanda',
    nome: 'Soluço Patriótico',
    tipo: 'cone',
    desc: 'Rajada de "HIC!" em cone: 10 de dano num alcance de 210 com EMPURRÃO forte, a cada 1,15s. Por nível: +30% de dano e recarga menor. Nv 3 alarga o cone, nv 5 dá 20% de chance de ATORDOAR, nv 6 solta soluço DUPLO, nv 7 empurra mais longe e nv 8 é soluço TRIPLO. Engasgou em rede nacional.',
    evolution: { passive: 'gabinete_odio', into: 'cadeia_nacional' },
    scale: (lvl) => ({
      damage: lin(10, 0.3)(lvl),
      cooldown: lin(1.15, -0.06)(lvl),
      range: lin(210, 0.06)(lvl),
      arc: (Math.PI / 3) * (lvl >= 3 ? 1.35 : 1),   // nv 3: cone mais largo
      knockback: lvl >= 7 ? 420 : 260,               // nv 7: empurrão maior
      stunChance: lvl >= 5 ? 0.2 : 0,                // nv 5: chance de atordoar
      stunDur: 0.8,
      bursts: lvl >= 8 ? 3 : lvl >= 6 ? 2 : 1,       // nv 6/8: soluço duplo/triplo
    }),
  },
  cachaca_51: {
    id: 'cachaca_51',
    nome: 'Pão com Mortadela',
    tipo: 'lob',
    desc: 'Arremessa um pão com mortadela que explode em farelos: 14 de dano num raio de 90, alcance 360, a cada 1,6s. Por nível: +30% de dano. Nv 3 deixa POÇA de mortadela (dps), nv 4 alonga a poça, nv 5 são 2 pães, nv 6 a poça dá LENTIDÃO, nv 7 amplia a explosão e nv 8 a poça ATRAI os inimigos. Lanche do comício, servido quente.',
    evolution: { passive: 'auxilio_brasil', into: 'refinaria_garapa' },
    scale: (lvl) => ({
      damage: lin(14, 0.3)(lvl),
      cooldown: lin(1.6, -0.05)(lvl),
      count: lvl >= 5 ? 2 : 1,                       // nv 5: dose dupla de mortadela
      range: 360,
      speed: 520,
      radius: lin(90, 0.1)(lvl) * (lvl >= 7 ? 1.3 : 1),
      puddleDps: lvl >= 3 ? lin(6, 0.3)(lvl) : 0,    // nv 3: nasce a poça
      puddleDur: lvl >= 3 ? (lvl >= 4 ? 3.4 : 2.2) : 0,
      slowMult: lvl >= 6 ? 0.55 : undefined,         // nv 6: lentidão na gordura
      pull: lvl >= 8 ? 70 : undefined,               // nv 8: o cheiro atrai
    }),
  },
  panelaco: {
    id: 'panelaco',
    nome: 'Estoque de Vento',
    tipo: 'gust',
    desc: 'Estoca e libera uma rajada de vento perfurante: 11 de dano com EMPURRÃO forte, alcance 420, a cada 1,5s. Por nível: +30% de dano e mais empurrão. Nv 4 vai mais longe, nv 5 solta rajadas LATERAIS, nv 6 tem 25% de chance de deixar um MINI-TORNADO, nv 7 recarrega mais rápido e nv 8 as rajadas RICOCHETEIAM nas bordas. Não foi má gestão, foi vento contrário.',
    evolution: { passive: 'pec_vinganca', into: 'usina_angra' },
    scale: (lvl) => ({
      damage: lin(11, 0.3)(lvl),
      cooldown: lin(1.5, -0.05)(lvl) * (lvl >= 7 ? 0.8 : 1),
      range: lin(420, 0.06)(lvl) * (lvl >= 4 ? 1.25 : 1),
      speed: 640,
      width: 46,
      knockback: lin(180, 0.15)(lvl),
      count: lvl >= 5 ? 3 : 1,                       // nv 5: rajadas laterais
      tornadoChance: lvl >= 6 ? 0.25 : 0,            // nv 6: mini-tornado
      tornadoDps: lin(12, 0.2)(lvl),
      tornadoDur: 2.5,
      tornadoPull: 90,
      tornadoRadius: 75,
      ricochet: lvl >= 8,                            // nv 8: quica nas bordas
    }),
  },
  pombas_da_paz: {
    id: 'pombas_da_paz',
    nome: 'Raízes da Amazônia',
    tipo: 'trap',
    desc: 'Raízes brotam sob a horda e PRENDEM os inimigos: 8 de dano/s num raio de 110 enquanto a raiz durar (1,6s), a cada 2,6s. Por nível: +30% de dano/s e raiz mais duradoura. Nv 4 prende mais gente, nv 5 brota mais longe, nv 6 os presos tomam +30% de dano de TODAS as armas, nv 7 tem 25% de chance de brotar uma ÁRVORE-barreira e nv 8 as raízes se espalham. A floresta cobra na raiz.',
    evolution: { passive: 'ze_gotinha', into: 'revoada_cop30' },
    scale: (lvl) => ({
      dps: lin(8, 0.3)(lvl),
      cooldown: lin(2.6, -0.06)(lvl),
      radius: 110 * (lvl >= 4 ? 1.3 : 1) * (lvl >= 8 ? 1.25 : 1),
      rootDur: lin(1.6, 0.12)(lvl),
      castRange: lvl >= 5 ? 500 : 340,               // nv 5: brota mais longe
      vuln: lvl >= 6,                                // nv 6: presos amolecem
      treeChance: lvl >= 7 ? 0.25 : 0,               // nv 7: árvore-barreira
      trees: 1,
      treeDur: 6,
      backlashChance: 0,
      backlashDur: 0,
    }),
  },
  carteira_trabalho: {
    id: 'carteira_trabalho',
    nome: 'Carteira de Trabalho Bumerangue',
    tipo: 'bounce',
    desc: 'CTPS-bumerangue: atravessa a fila do emprego causando 14 de dano, voa até 230, dá meia-volta e assina todo mundo DE NOVO com dano DOBRADO no retorno. Recarga de 1,55s. Por nível: +30% de dano e +velocidade. Nv 3 aumenta o dano da volta, nv 4 atravessa sem limite, nv 5 são 2 carteiras, nv 6 deixa PAPÉIS cortantes no caminho e nv 8 explode em documentos ao voltar pra mão.',
    evolution: { passive: 'passaporte', into: 'mei_supremo' },
    scale: (lvl) => ({
      damage: lin(14, 0.3)(lvl),
      cooldown: lin(1.55, -0.06)(lvl),
      count: lvl >= 5 ? 2 : 1,                       // nv 5: contratação em massa
      bounces: 0,
      speed: lin(340, 0.08)(lvl),                    // nv 2+: CLT com pressa
      bounceBonus: 0,
      split: false,
      pierce: true,              // fura a fila — CLT não quica, atravessa
      boomerang: true,           // contrato temporário: vai e volta
      range: lin(230, 0.12)(lvl),
      returnMult: lvl >= 3 ? 2.5 : 2,                // dano da volta (nv 3: +25%)
      maxHits: lvl >= 4 ? 0 : 4,                     // nv 4: atravessa sem teto
      trailDps: lvl >= 6 ? 8 : 0,                    // nv 6: papéis cortantes
      finalBlast: lvl >= 8 ? 110 : 0,                // nv 8: explosão de documentos
      // Teleguiada pro desempregado mais próximo (#106): com 'facing', fugir e
      // mirar eram a mesma alavanca — só acertava quem corria pra CIMA da horda
      aim: 'nearest',
    }),
  },
  cruz_credo: {
    id: 'cruz_credo',
    nome: 'Cruz Credo',
    tipo: 'orbit',
    desc: '2 cruzes abençoadas orbitam coladas no candidato (raio 70) exorcizando quem encosta: 40 de dano/s enquanto a cruz passa por cima do pecador. Por nível: +25% de dano/s. Nv 3 gira mais rápido, nv 4 aumenta as cruzes, nv 5 soma +1 cruz, nv 6 as cruzes EMPURRAM, nv 7 o rastro de luz queima mais e nv 8 fecha o rosário com a 4ª cruz.',
    evolution: { passive: 'foro_privilegiado', into: 'concordata_divina' },
    scale: (lvl) => ({
      // dps alto porque o dano só vale enquanto a cruz passa por cima do
      // pecador (setor angular estreito): a média fica na linha das iniciais
      dps: lin(40, 0.25)(lvl) * (lvl >= 7 ? 1.2 : 1),
      beams: 2 + (lvl >= 5 ? 1 : 0) + (lvl >= 8 ? 1 : 0),  // 2 a 4 cruzes
      radius: lin(70, 0.08)(lvl),
      beamLen: 30 * (lvl >= 4 ? 1.4 : 1),            // nv 4: cruzes maiores
      rotSpeed: 2.6 * (lvl >= 3 ? 1.25 : 1),         // nv 3: procissão acelera
      push: lvl >= 6 ? 500 : 0,                      // nv 6: a fé empurra
    }),
  },
  vampiros_madrugada: {
    id: 'vampiros_madrugada',
    nome: 'Morceguinhos da Madrugada',
    tipo: 'leech',
    desc: 'Invoca 2 morceguinhos que GRUDAM no inimigo sugando 9 de vida/s até o óbito — e 15% da sangria volta como CURA. Caçam a até 520; nova leva a cada 2s. Por nível: +30% de sangria e mais velocidade. Nv 4 dobra o roubo de vida, nv 5 soma +1 morcego, nv 6 eles EMENDAM a próxima mordida sem voltar, nv 7 explodem em SOMBRA quando o alvo morre e nv 8 priorizam o inimigo mais gordo.',
    evolution: { passive: 'impeachment_express', into: 'reforma_previdencia' },
    scale: (lvl) => ({
      dps: lin(9, 0.3)(lvl),
      cooldown: lin(2.0, -0.07)(lvl),
      count: lvl >= 5 ? 3 : 2,                       // nv 5: mais morcegos
      speed: lin(400, 0.08)(lvl),                    // nv 3+: voam mais rápido
      range: 520,
      healRatio: lvl >= 4 ? 0.3 : 0.15,              // nv 4: dobra o roubo de vida
      chain: lvl >= 6,                               // nv 6: emenda a mordida
      deathBlast: lvl >= 7 ? 60 : 0,                 // nv 7: explosão de sombra
      deathBlastDmg: lvl >= 7 ? lin(18, 0.2)(lvl) : 0,
      targetHighHp: lvl >= 8,                        // nv 8: mira no mais gordo
    }),
  },
  cadeirada: {
    id: 'cadeirada',
    nome: 'Cadeirada Ao Vivo',
    tipo: 'lob',
    desc: 'Arremessa uma cadeira que estilhaça no pouso: 17 de dano num raio de 85, alcance 340, a cada 1,7s. Por nível: +30% de dano. Nv 3 a cadeira ATRAVESSA um inimigo no voo, nv 4 solta mais lascas, nv 5 ganha EMPURRÃO, nv 6 são 2 cadeiras, nv 7 as lascas ficam no chão dando dps e nv 8 o impacto ATORDOA. Direto do debate, sem cortes.',
    evolution: { passive: 'ima_votos', into: 'brasil_urgente' },
    scale: (lvl) => ({
      damage: lin(17, 0.3)(lvl) * (lvl >= 4 ? 1.2 : 1),  // nv 4: mais lascas
      cooldown: lin(1.7, -0.06)(lvl),
      count: lvl >= 6 ? 2 : 1,                       // nv 6: o estúdio inteiro voa
      range: 340,
      speed: 560,
      radius: lin(85, 0.12)(lvl) * (lvl >= 4 ? 1.15 : 1),
      puddleDps: lvl >= 7 ? lin(8, 0.2)(lvl) : 0,    // nv 7: lascas no chão
      puddleDur: lvl >= 7 ? 2.5 : 0,
      knockback: lvl >= 5 ? 260 : 0,                 // nv 5: empurrão de auditório
      stun: lvl >= 8 ? 0.7 : 0,                      // nv 8: nocaute ao vivo
      flightDamage: lvl >= 3 ? lin(10, 0.3)(lvl) : 0, // nv 3: atropela no voo
      flightHits: 1,
    }),
  },

  // --------------------------------------------------------------------------
  // ARMAS ADQUIRÍVEIS NA URNA ELETRÔNICA HACKEADA
  // --------------------------------------------------------------------------
  fake_news: {
    id: 'fake_news',
    nome: 'Fake News',
    tipo: 'bounce',
    desc: 'Projétil teleguiado que SE MULTIPLICA a cada quique: 8 de dano, 2 quiques e até 3 cópias da mentira, a cada 1,4s. Por nível: +30% de dano, +1 cópia e recarga menor; níveis 2 e 4 somam +1 quique. Mentira contada 100 vezes vira meta de dano.',
    evolution: { passive: 'robos_whatsapp', into: 'deep_fake' },
    scale: (lvl) => ({
      damage: lin(8, 0.3)(lvl),
      cooldown: lin(1.4, -0.09)(lvl),
      count: 1,
      bounces: 2 + Math.floor(lvl / 2),
      speed: 400,
      bounceBonus: 0,
      split: true,               // cada quique cria uma cópia da mentira
      maxSplits: 2 + lvl,
      aim: 'nearest',            // mentira sempre acha um alvo
    }),
  },
  lulinha: {
    id: 'lulinha',
    nome: 'Faz o L',
    tipo: 'bounce',
    desc: 'Mãozinha fazendo o L que voa reta e ricocheteia entre inimigos: 11 de dano, 3 quiques, um disparo a cada 0,95s; cada quique soma +15% de dano ("pleno emprego"). Por nível: +30% de dano, +1 quique e recarga menor; níveis 2 e 4 somam +1 mão no leque (máx 3). Faz o L que a horda sente.',
    scale: (lvl) => ({
      damage: lin(11, 0.3)(lvl),
      cooldown: lin(0.95, -0.09)(lvl),
      count: Math.min(1 + Math.floor(lvl / 2), 3),
      bounces: 2 + lvl,          // quica mais que discurso em palanque
      speed: 430,
      bounceBonus: 0.15,         // +15% dano por quique — o "pleno emprego"
      split: false,
      aim: 'facing',             // voa reto na direção das setas — mão não tem GPS
    }),
  },
  pix_emendas: {
    id: 'pix_emendas',
    nome: 'Aliados Políticos',
    tipo: 'summon',
    desc: 'Invoca via orçamento secreto um aliado político com 45 de HP e 9 de dano/s, que luta por 14s; até 2 na base ao mesmo tempo, novo repasse a cada 7s. Por nível: +30% de HP e de dano dos aliados e recarga menor; níveis 3 e 5 somam +1 vaga. Fisiologismo aplicado.',
    scale: (lvl) => ({
      cooldown: lin(7, -0.13)(lvl),
      allyHp: lin(45, 0.3)(lvl),
      allyDps: lin(9, 0.3)(lvl),
      maxAllies: Math.min(1 + Math.floor((lvl + 1) / 2), 4),
      duration: 14,
    }),
  },
  cpi_giratoria: {
    id: 'cpi_giratoria',
    nome: '#FiqueEmCasa',
    tipo: 'orbit',
    desc: 'Seringas de vacina orbitando ao seu redor: doses de 130 de comprimento girando a 140 do candidato, imunizando à força quem chega perto com 12 de dano/s. Por nível: +40% de dano/s, +15% de órbita e +20% de alcance da dose; níveis 2 e 4 somam +1 seringa (máx 3). Ciência a domicílio.',
    scale: (lvl) => ({
      dps: lin(12, 0.4)(lvl),
      beams: Math.min(1 + Math.floor(lvl / 2), 3),
      radius: lin(140, 0.15)(lvl),
      beamLen: lin(130, 0.2)(lvl),
      rotSpeed: 1.6,             // rad/s — velocidade de campanha de vacinação
    }),
  },
  rachadinha: {
    id: 'rachadinha',
    nome: 'ICMS',
    tipo: 'drain',
    desc: 'Aura de imposto que drena 6 de vida/s de TODO inimigo num raio de 150 e "devolve" metade pra você (teto de cura: 8 HP/s — o TCU limitou o repasse). Por nível: +40% de dreno, +18% de raio e teto de cura maior. Tributação na fonte, em tempo real.',
    scale: (lvl) => ({
      dps: lin(6, 0.4)(lvl),
      radius: lin(150, 0.18)(lvl),
      healRatio: 0.5,            // metade volta pro "gabinete"
      healCap: lin(8, 0.35)(lvl), // TCU limitou o repasse: cura máxima em HP/s
    }),
  },
  mensalao: {
    id: 'mensalao',
    nome: 'Mensalão',
    tipo: 'cone',
    desc: 'Atira maços de dinheiro em cone de 72 graus: 22 de dano num alcance de 280, a cada 2,4s. Por nível: +40% de dano, +18% de alcance e recarga menor. Pagamento em espécie, direto na cara da horda.',
    scale: (lvl) => ({
      damage: lin(22, 0.4)(lvl),
      cooldown: lin(2.4, -0.1)(lvl),
      range: lin(280, 0.18)(lvl),
      arc: Math.PI / 2.5,
    }),
  },
  oito_jan: {
    id: 'oito_jan',
    nome: '8 de Janeiro',
    tipo: 'nova',
    desc: 'Explosão de horda invasora ao seu redor: 26 de dano num raio de 230 com knockback, a cada 5,5s. Por nível: +45% de dano, +18% de raio e recarga menor. Quebra tudo — inclusive relógio histórico e vidraça do STF.',
    scale: (lvl) => ({
      damage: lin(26, 0.45)(lvl),
      cooldown: lin(5.5, -0.12)(lvl),
      radius: lin(230, 0.18)(lvl),
      knockback: 200,
    }),
  },
  stf: {
    id: 'stf',
    nome: 'Decisão do STF',
    tipo: 'freeze',
    desc: 'Decisão monocrática congela inimigos num raio de 260 por 1,8s e causa 6 de dano, a cada 6,5s. Por nível: +20% de duração, +15% de raio, +30% de dano e recarga menor. Efeito colateral: 8% de chance de congelar VOCÊ por 1s (sigilo de 100 anos no motivo).',
    scale: (lvl) => ({
      cooldown: lin(6.5, -0.12)(lvl),
      radius: lin(260, 0.15)(lvl),
      duration: lin(1.8, 0.2)(lvl),
      damage: lin(6, 0.3)(lvl),
      selfFreezeChance: 0.08,
      selfFreezeDur: 1.0,
    }),
  },
} satisfies Record<string, WeaponDef>;

export type WeaponId = keyof typeof WEAPONS;

// -----------------------------------------------------------------------------
// ARMAS SUPREMAS (issues #98/#121) — nível único, entregues pelo GOLPE DE
// MESTRE do Cassino da Maleta quando a receita (arma nv 8 + passiva) está
// cumprida. Ordem de grandeza: 2-3x a eficácia da arma base no nível máximo.
// Os ícones reaproveitam o da arma base (nada de emoji novo — ver CLAUDE.md).
// -----------------------------------------------------------------------------
export const EVOLVED_WEAPONS = {
  cadeia_nacional: {
    id: 'cadeia_nacional',
    nome: 'Crise de Soluço Nacional',
    tipo: 'cone',
    evolved: true,
    desc: 'O soluço sai do controle: 5 rajadas de "HIC!" em direções aleatórias por disparo, cada uma empurrando e com 30% de chance de ATORDOAR. O país inteiro engasgado em cadeia nacional.',
    scale: () => ({
      damage: 32,
      cooldown: 1.3,
      range: 270,
      arc: Math.PI / 2.6,
      knockback: 460,
      stunChance: 0.3,
      stunDur: 1,
      bursts: 5,
      scatter: true,             // rajadas descontroladas em volta
    }),
  },
  refinaria_garapa: {
    id: 'refinaria_garapa',
    nome: 'Banquete do Companheiro',
    tipo: 'lob',
    evolved: true,
    desc: 'CHOVE pão com mortadela: 3 pães por leva que explodem em poças de gordura ENORMES, com lentidão e sucção — as poças se emendam num tapete de dano contínuo. Churrasco de campanha pra multidão.',
    scale: () => ({
      damage: 40, cooldown: 1.1, count: 3, range: 420, speed: 560,
      radius: 140, puddleDps: 26, puddleDur: 7, spread: 2,
      slowMult: 0.5, pull: 80,
    }),
  },
  usina_angra: {
    id: 'usina_angra',
    nome: 'Tornado da Nova Matriz',
    tipo: 'gust',
    evolved: true,
    desc: 'A nova matriz energética atinge criticidade: 2 TORNADOS errantes por disparo giram pelo mapa puxando a horda pra dentro e moendo com dano contínuo. Estocou vento demais, devolve em espiral.',
    scale: () => ({
      damage: 0, cooldown: 4.0, range: 0, speed: 120, width: 0,
      knockback: 0, count: 2, tornadoChance: 1,
      tornadoDps: 30, tornadoDur: 6, tornadoPull: 150, tornadoRadius: 95,
      ricochet: false, tornado: true,
    }),
  },
  revoada_cop30: {
    id: 'revoada_cop30',
    nome: 'Floresta em Pé',
    tipo: 'trap',
    evolved: true,
    desc: 'A raiz vira mata fechada: prende multidões num raio de 150, os presos tomam +30% de dano de tudo, e TRÊS árvores-barreira brotam segurando o avanço da horda. Desmatou, plantou de volta — na cabeça deles.',
    scale: () => ({
      dps: 32, cooldown: 2.2, radius: 150, rootDur: 2.6, castRange: 520,
      vuln: true, treeChance: 1, trees: 3, treeDur: 7,
      backlashChance: 0, backlashDur: 0,
    }),
  },
  mei_supremo: {
    id: 'mei_supremo',
    nome: 'CLT Reverso 6x1',
    tipo: 'bounce',
    evolved: true,
    desc: 'Pejotização total: ENXAME de 6 carteiras-bumerangue em todas as direções, atravessando o país inteiro, deixando papéis cortantes no caminho e explodindo em documentos na volta. Escala 6x1: seis carteiras, um sobrevivente.',
    scale: () => ({
      damage: 45, cooldown: 0.85, count: 6, bounces: 0, speed: 430,
      bounceBonus: 0, split: false, pierce: true, boomerang: true,
      range: 460, returnMult: 2, maxHits: 0, trailDps: 10, finalBlast: 120,
      radial: true, aim: 'facing',
    }),
  },
  concordata_divina: {
    id: 'concordata_divina',
    nome: 'Procissão Giratória',
    tipo: 'orbit',
    evolved: true,
    desc: 'Dois anéis de cruzes em rotação contrária — um colado, outro afastado — que EMPURRAM os fiéis relapsos e ainda REFLETEM projéteis hostis. A quermesse virou zona de exclusão litúrgica.',
    scale: () => ({
      dps: 130, beams: 3, radius: 85, beamLen: 34, rotSpeed: 3.0,
      rings: 2, reflect: true, push: 500,
    }),
  },
  reforma_previdencia: {
    id: 'reforma_previdencia',
    nome: 'Conclave Vampírico',
    tipo: 'leech',
    evolved: true,
    desc: 'Os morceguinhos viram vampirinhos de terno: SEIS em regime de urgência, emendando mordidas sem voltar, explodindo em sombra a cada óbito — e 30% da sangria vira saúde SUA. Votação secreta, sangue público.',
    scale: () => ({
      dps: 55, cooldown: 0.7, count: 6, speed: 520, range: 720,
      healRatio: 0.3, chain: true, deathBlast: 70, deathBlastDmg: 22,
      targetHighHp: true,
    }),
  },
  brasil_urgente: {
    id: 'brasil_urgente',
    nome: 'Cadeira de Auditório',
    tipo: 'lob',
    evolved: true,
    desc: 'A plateia enfurecida entra na briga: QUATRO cadeiras despencam por leva, com empurrão, atordoamento e lascas dobradas forrando o chão. O auditório inteiro voa em rede nacional.',
    scale: () => ({
      damage: 55, cooldown: 1.0, count: 4, range: 480, speed: 700,
      radius: 125, puddleDps: 18, puddleDur: 3, spread: 1,
      knockback: 220, stun: 0.6, flightDamage: 30, flightHits: 2,
    }),
  },
  deep_fake: {
    id: 'deep_fake',
    nome: 'Deep Fake',
    tipo: 'bounce',
    evolved: true,
    desc: 'A mentira agora tem inteligência artificial: projéteis teleguiados que se dividem DUAS vezes. Ninguém mais sabe o que é real.',
    scale: () => ({
      damage: 26, cooldown: 0.85, count: 2, bounces: 5, speed: 460,
      bounceBonus: 0, split: true, maxSplits: 8, splitGen: 2,
      homing: true, aim: 'nearest',
    }),
  },
} satisfies Record<string, WeaponDef>;

export type EvolvedWeaponId = keyof typeof EVOLVED_WEAPONS;

// Lookup por id vindo da rede (string qualquer) — inclui as armas supremas
export const WEAPON_TABLE: Record<string, WeaponDef> = { ...WEAPONS, ...EVOLVED_WEAPONS };

export const WEAPON_LIST: WeaponDef[] = Object.values(WEAPON_TABLE);

// Armas que podem aparecer na urna. Além das 8 exclusivas da urna, TODAS as
// armas iniciais dos personagens entram no pool: jogando de Dilma dá pra achar
// a cadeirada, o soluço, a cruz etc. A sua própria inicial é excluída sozinha
// (você já a tem) e a base cuja suprema você já carrega fica de fora
// (anti-beco-sem-saída — ambas as regras já rodam no ProgressionSystem).
export const URNA_WEAPON_POOL: WeaponId[] = [
  // exclusivas da urna
  'fake_news', 'pix_emendas', 'cpi_giratoria', 'rachadinha', 'mensalao', 'oito_jan', 'stf', 'lulinha',
  // armas iniciais dos personagens, agora também no pool
  'live_varanda', 'cachaca_51', 'panelaco', 'cadeirada',
  'pombas_da_paz', 'carteira_trabalho', 'cruz_credo', 'vampiros_madrugada',
];
